import { Button, Form, Input, Modal, Radio, message } from 'antd';
import { useEffect, useState, useCallback } from 'react';
import './CouponModal.scss';


const CouponModal = ({ 
  visible, 
  onClose, 
  isEditing, 
  editingCoupon, 
  setCoupons, 
  coupons, 
  editingCouponId
}) => {
  const [form] = Form.useForm();
  const [nameCoupon, setNameCoupon] = useState('');
  const [codeCoupon, setCodeCoupon] = useState('');
  const [typeCoupon, setTypeCoupon] = useState('PERCENTAGE');
  const [valueCoupon, setValueCoupon] = useState(0);

  const resetForm = useCallback(() => {
    setNameCoupon('');
    setCodeCoupon('');
    setTypeCoupon('PERCENTAGE');
    setValueCoupon(0);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (isEditing && editingCoupon) {
      const displayValue = editingCoupon.type === 'FIXED' ? editingCoupon.value / 100 : editingCoupon.value;
      setNameCoupon(editingCoupon.name);
      setCodeCoupon(editingCoupon.code);
      setTypeCoupon(editingCoupon.type);
      setValueCoupon(displayValue);

      form.setFieldsValue({
        name: editingCoupon.name,
        code: editingCoupon.code,
        type: editingCoupon.type,
        value: displayValue,
      });
    } else {
      resetForm();
    }
  }, [isEditing, editingCoupon, form, resetForm]);

  const handleSubmit = () => {
    form.validateFields().then(() => {
      const couponData = {
        id: isEditing ? editingCouponId : new Date().getTime(),
        name: nameCoupon,
        code: codeCoupon.toUpperCase(),
        type: typeCoupon,
        value: typeCoupon === 'FIXED' ? Math.floor(valueCoupon * 100) : valueCoupon, // Store fixed values in cents
        new: !isEditing, // Mark as new if creating, not if editing
      };

      // Check if code already exists (excluding current coupon if editing)
      const codeExists = coupons.some(coupon => 
        coupon.code.toLowerCase() === codeCoupon.toLowerCase() && 
        (!isEditing || coupon.id !== editingCouponId)
      );

      if (codeExists) {
        message.error('Este código de cupom já existe. Escolha outro código.');
        return;
      }

      if (isEditing) {
        const updatedCoupons = coupons.map(coupon =>
          coupon.id === editingCouponId ? couponData : coupon
        );
        setCoupons(updatedCoupons);
        message.success('Cupom atualizado com sucesso!');
      } else {
        setCoupons([...coupons, couponData]);
        message.success('Cupom criado com sucesso!');
      }

      handleClose();
    }).catch(() => {
      message.error('Por favor, preencha todos os campos obrigatórios.');
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleValueChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    
    // Validate percentage (0-100)
    if (typeCoupon === 'PERCENTAGE' && value > 100) {
      setValueCoupon(100);
      form.setFieldValue('value', 100);
      return;
    }
    
    // Validate fixed value (min 0)
    if (value < 0) {
      setValueCoupon(0);
      form.setFieldValue('value', 0);
      return;
    }
    
    setValueCoupon(value);
  };

  const generateRandomCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCodeCoupon(code);
    form.setFieldValue('code', code);
  };

  return (
    <Modal
      title={isEditing ? "Editar Cupom de Desconto" : "Criar Cupom de Desconto"}
      open={visible}
      onCancel={handleClose}
      className="coupon-modal"
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEditing ? 'Atualizar' : 'Criar'} Cupom
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" className="coupon-form">
        <Form.Item
          label="Nome do Cupom"
          name="name"
          rules={[
            { required: true, message: 'Por favor, insira o nome do cupom' },
            { min: 3, message: 'O nome deve ter pelo menos 3 caracteres' },
            { max: 50, message: 'O nome deve ter no máximo 50 caracteres' }
          ]}
        >
          <Input
            value={nameCoupon}
            onChange={(e) => setNameCoupon(e.target.value)}
            placeholder="Ex: Desconto de Lançamento"
          />
        </Form.Item>

        <Form.Item
          label="Código do Cupom"
          name="code"
          rules={[
            { required: true, message: 'Por favor, insira o código do cupom' },
            { min: 3, message: 'O código deve ter pelo menos 3 caracteres' },
            { max: 20, message: 'O código deve ter no máximo 20 caracteres' },
            { pattern: /^[A-Z0-9_]+$/, message: 'Use apenas letras maiúsculas, números e underscore' }
          ]}
        >
          <div className="code-input-group">
            <Input
              value={codeCoupon}
              onChange={(e) => setCodeCoupon(e.target.value.toUpperCase())}
              placeholder="Ex: DESCONTO10"
              style={{ textTransform: 'uppercase' }}
            />
            <Button type="default" onClick={generateRandomCode}>
              Gerar
            </Button>
          </div>
        </Form.Item>

        <Form.Item
          label="Tipo de Desconto"
          name="type"
          rules={[{ required: true, message: 'Por favor, selecione o tipo de desconto' }]}
        >
          <Radio.Group
            value={typeCoupon}
            onChange={(e) => {
              setTypeCoupon(e.target.value);
              setValueCoupon(0);
              form.setFieldValue('value', 0);
            }}
          >
            <Radio value="PERCENTAGE">Porcentagem (%)</Radio>
            <Radio value="FIXED">Valor Fixo (R$)</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={typeCoupon === 'PERCENTAGE' ? 'Porcentagem de Desconto' : 'Valor do Desconto (R$)'}
          name="value"
          rules={[
            { required: true, message: 'Por favor, insira o valor do desconto' },
            { 
              validator: (_, value) => {
                if (typeCoupon === 'PERCENTAGE' && (value <= 0 || value > 100)) {
                  return Promise.reject(new Error('A porcentagem deve estar entre 1% e 100%'));
                }
                if (typeCoupon === 'FIXED' && value <= 0) {
                  return Promise.reject(new Error('O valor deve ser maior que zero'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            type="number"
            min={0}
            max={typeCoupon === 'PERCENTAGE' ? 100 : undefined}
            step={typeCoupon === 'PERCENTAGE' ? 1 : 0.01}
            value={valueCoupon || ''}
            onChange={handleValueChange}
            placeholder={typeCoupon === 'PERCENTAGE' ? 'Ex: 10' : 'Ex: 15.00'}
            addonAfter={typeCoupon === 'PERCENTAGE' ? '%' : 'R$'}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CouponModal;